import { CSSResultGroup, html, LitElement } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { tabGroupStyle, tabPanelStyle, tabStyle } from './tabs.style.js'
import { classMap } from 'lit/directives/class-map.js'
import { fontStyles } from 'elements/_shared/fonts.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'


@customElement('cse-tab-panel')
export class CSETabPanel extends LitElement {
  static override styles: CSSResultGroup = [fontStyles, hostStyle, tabPanelStyle]
  @property({ type: String })
  name = ''

  @property({ type: Boolean })
  selected = false

  override render() {
    return html`
      <div class=${classMap({
          'cse-tab-panel': true,
          'selected': this.selected,
          'font-body': true,
        })}
        id="tab-panel-${this.name}"
        role="tabpanel"
      >
      <slot></slot>
    </div>
    `
  }
}

@customElement('cse-tab')
export class CSETab extends LitElement {
  static override styles: CSSResultGroup = [fontStyles, hostStyle, tabStyle]

  @property({ type: String })
  panel = ''

  @property({ type: Boolean })
  selected = false

  @property({ type: Boolean })
  disabled = false

  override render() {
    return html`
      <button class="${classMap({
          'cse-tab': true,
          'selected': this.selected,
          'disabled': this.disabled
        })}"
        aria-selected=${this.selected}
        aria-controls="tab-panel-${this.panel}"
        role="tab"
      >
        <slot name="prefix"></slot>
        <slot class="font-body"></slot>
        <slot name="suffix"></slot>
      </button>
    `
  }
}

@customElement('cse-tabs')
export class CSETabs extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, tabGroupStyle]

  @state()
  private selectedTab: CSETab | null = null

  @state()
  private tabs!: Array<CSETab>

  @state()
  private panels!: Array<CSETabPanel>

  override connectedCallback() {
    super.connectedCallback()

    this.tabs = Array.from(this.querySelectorAll('cse-tab'))
    this.panels = Array.from(this.querySelectorAll('cse-tab-panel'))

    if(this.tabs && this.tabs[0]) {
      this.setSelectedTab(this.tabs[0])
    }
  }

  private setSelectedTab(tab: CSETab) {
    this.selectedTab = tab
    this.tabs.forEach(tab => (tab.selected = tab === this.selectedTab))
    this.panels.forEach(panel => (panel.selected = panel.name === this.selectedTab?.panel))
  }

  private handleTabsClick(event: MouseEvent) {
    const target = event.target as HTMLElement
    const tab = target.closest('cse-tab')
    const tabGroup = tab?.closest('cse-tabs')

    // Ensure the target tab is in this tab group
    if (tabGroup !== this) {
      return
    }

    if (tab !== null && !tab.disabled) {
      this.setSelectedTab(tab)
    }
  }

  override render() {
    return html`
      <div
        class="cse-tabs"
        @click="${this.handleTabsClick}"
        role="tablist"
      >
        <div class="tabs-wrapper">
          <div class="tabs-scroll">
            <slot></slot>
          </div>
        </div>
        <div class="content-wrapper">
          <slot name="content"></slot>
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cse-tab': CSETab
    'cse-tabs': CSETabs
    'cse-tab-panel': CSETabPanel
  }
}
